import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import * as APIModels from "../../../../models";
import { DataService } from "../../../../services/DataService";

type PartyTypeOption = { label: string; value: string };

// Adds a filing party to an existing case from the queue: a person's name and/or an
// organization name, plus a party type. The caller decides how the new party is
// keyed and stored.
export default function AddFilingPartyModal({
    show,
    onClose,
    onSave,
    caseData,
}: {
    show: boolean;
    onClose: () => void;
    onSave: (party: APIModels.Party) => void;
    caseData?: APIModels.CaseData;
}) {
    const [firstName, setFirstName] = useState("");
    const [middleName, setMiddleName] = useState("");
    const [lastName, setLastName] = useState("");
    const [organization, setOrganization] = useState("");
    const [partyType, setPartyType] = useState<PartyTypeOption | null>(null);
    const [partyTypeOptions, setPartyTypeOptions] = useState<PartyTypeOption[]>(
        []
    );
    const [error, setError] = useState("");

    // Party types depend on the case type, which the case search sets.
    const caseTypeId =
        caseData?.caseSubTypeID &&
        caseData.caseSubTypeID !== APIModels.EmptyGuid
            ? caseData.caseSubTypeID
            : caseData?.caseTypeID ?? "";

    useEffect(() => {
        if (!show) return;
        setPartyTypeOptions([]);
        if (!caseTypeId || caseTypeId === APIModels.EmptyGuid) return;
        DataService.getPartyTypes({ caseTypeId }).then(
            (resp) =>
                setPartyTypeOptions(
                    resp.map((t) => ({
                        label: t.description ?? "",
                        value: t.ePortalID ?? "",
                    }))
                ),
            (err) => console.log(err)
        );
    }, [show, caseTypeId]);

    const reset = () => {
        setFirstName("");
        setMiddleName("");
        setLastName("");
        setOrganization("");
        setPartyType(null);
        setError("");
    };

    const onCancel = () => {
        reset();
        onClose();
    };

    const onSaveClick = () => {
        if (!partyType) {
            setError("Select a party type.");
            return;
        }
        if (!organization.trim() && !(firstName.trim() && lastName.trim())) {
            setError("Enter a first and last name, or an organization name.");
            return;
        }

        onSave({
            ...new APIModels.Party(),
            firstName: firstName.trim() || undefined,
            middleName: middleName.trim() || undefined,
            lastName: lastName.trim() || undefined,
            name: organization.trim() || undefined,
            partyType: partyType.label,
            partyTypeCode: partyType.value,
        });
        reset();
    };

    return (
        <Modal show={show} size="lg" centered onHide={onCancel}>
            <Modal.Header>
                <Modal.Title>Add Filing Party</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row mb-3">
                    <div className="col-sm-4">
                        <label
                            htmlFor="txtFilingPartyFirstName"
                            className="form-label"
                        >
                            First Name
                        </label>
                        <input
                            id="txtFilingPartyFirstName"
                            type="text"
                            className="form-control"
                            placeholder="First Name"
                            value={firstName}
                            onChange={(e) =>
                                setFirstName(e.currentTarget.value)
                            }
                        />
                    </div>
                    <div className="col-sm-4">
                        <label
                            htmlFor="txtFilingPartyMiddleName"
                            className="form-label"
                        >
                            Middle Name
                        </label>
                        <input
                            id="txtFilingPartyMiddleName"
                            type="text"
                            className="form-control"
                            placeholder="Middle Name"
                            value={middleName}
                            onChange={(e) =>
                                setMiddleName(e.currentTarget.value)
                            }
                        />
                    </div>
                    <div className="col-sm-4">
                        <label
                            htmlFor="txtFilingPartyLastName"
                            className="form-label"
                        >
                            Last Name
                        </label>
                        <input
                            id="txtFilingPartyLastName"
                            type="text"
                            className="form-control"
                            placeholder="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.currentTarget.value)}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <label
                            htmlFor="txtFilingPartyOrganization"
                            className="form-label"
                        >
                            Organization Name
                        </label>
                        <input
                            id="txtFilingPartyOrganization"
                            type="text"
                            className="form-control"
                            placeholder="Organization Name"
                            value={organization}
                            onChange={(e) =>
                                setOrganization(e.currentTarget.value)
                            }
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-6">
                        <label
                            htmlFor="selFilingPartyType"
                            className="form-label"
                        >
                            Party Type
                        </label>
                        <Select
                            inputId="selFilingPartyType"
                            options={partyTypeOptions}
                            value={partyType}
                            onChange={(value: any) =>
                                setPartyType(value as PartyTypeOption | null)
                            }
                            placeholder="Select the party type"
                            noOptionsMessage={() =>
                                caseTypeId && caseTypeId !== APIModels.EmptyGuid
                                    ? "No party types for this case type"
                                    : "Search the case first to load party types"
                            }
                        />
                    </div>
                </div>
                {error && (
                    <div style={{ color: "red", textAlign: "center" }}>
                        {error}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onSaveClick}>Save</Button>
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
